import { useEffect, useState } from 'react'
import ReactModal from 'react-modal'
import { X } from 'react-feather'
import classNames from 'classnames'

/*
We are adding this here as the __next element
is not available in the test environment
*/
if (process.env.NODE_ENV !== 'test') ReactModal.setAppElement('#__next')

type Props = {
  isOpen: boolean
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>
  width?: string
  children: JSX.Element | JSX.Element[]
  shouldCloseOnEsc?: boolean
  className?: string
  // Applied to the outer dialog card (border, shadow, padding, close icon
  // and content together) rather than the inner content wrapper `className`
  // targets — use this to anchor something that should highlight/spotlight
  // the whole modal.
  containerClassName?: string
  id?: string
  isCrossIconShown?: boolean
  isOverflowHidden?: boolean
  isOverflowAuto?: boolean
  handleCrossIconClick?: () => void
  containerStyle?: React.CSSProperties
  // Opt-in: close when the backdrop is clicked. Default off so form modals
  // don't close on an accidental outside click and lose the user's input.
  closeOnOutsideClick?: boolean
  // Override the overlay's stacking order. Used by classroom popups that
  // need to render above the product tour's dimming overlay.
  overlayZIndex?: number
  contentPadding?: string
  // Opt-in: fade + scale the dialog in on open. Default off so existing
  // modals keep their current instant-open behavior unless they ask for it.
  animated?: boolean
}

const Modal = ({
  isOpen,
  setIsOpen,
  width = 'sm:max-w-lg',
  children,
  shouldCloseOnEsc = true,
  className = 'bg-white',
  containerClassName = '',
  id = '',
  isCrossIconShown = false,
  isOverflowHidden = true,
  isOverflowAuto = true,
  handleCrossIconClick = () => setIsOpen(false),
  containerStyle,
  closeOnOutsideClick = false,
  overlayZIndex = 150,
  contentPadding = 'px-4 pt-5 pb-4 sm:p-10',
  animated = false,
}: Props): JSX.Element => {
  const [hasEntered, setHasEntered] = useState(false)

  useEffect(() => {
    if (!animated) return undefined
    if (!isOpen) {
      setHasEntered(false)
      return undefined
    }
    const frame = requestAnimationFrame(() => setHasEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [animated, isOpen])

  return (
    <ReactModal
      className="fixed inset-0 inset-x-0 flex items-start sm:items-center justify-center overflow-y-auto px-4 py-4 outline-none"
      overlayClassName="fixed inset-0 bg-smoke"
      style={{ overlay: { zIndex: overlayZIndex } }}
      id={id}
      isOpen={isOpen}
      shouldCloseOnEsc={shouldCloseOnEsc}
      onRequestClose={() => setIsOpen(false)}
      closeTimeoutMS={animated ? 200 : 0}
    >
      <>
        {/* Backdrop. The dialog below is a sibling, so clicks inside it don't
            bubble here — only clicks on the surrounding area close the modal. */}
        <div
          className="fixed inset-0 transition-opacity"
          onClick={closeOnOutsideClick ? () => setIsOpen(false) : undefined}
        >
          <div className="absolute inset-0 opacity-75 bg-grey"></div>
        </div>
        <div
          className={classNames(
            'bg-white rounded-lg shadow-xl transform transition-all sm:w-full',
            width,
            isOverflowHidden && 'overflow-hidden',
            containerClassName,
            animated &&
              classNames(
                'duration-200 ease-out',
                hasEntered ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              )
          )}
          style={containerStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <div
            className={`${className} ${
              isOverflowAuto && 'overflow-auto'
            } ${contentPadding} rounded-lg`}
            style={{ maxHeight: '90vh' }}
          >
            {isCrossIconShown && (
              <button
                onClick={handleCrossIconClick}
                className="absolute top-0 right-0 w-5 mt-2 mr-3 cursor-pointer md:mt-4 md:mr-5 text-grey-800 focus:outline-none hover:text-orange z-50"
                aria-labelledby="modal-x-button"
              >
                <X />
              </button>
            )}
            {children}
          </div>
        </div>
      </>
    </ReactModal>
  )
}

export default Modal
